import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { API, apiError } from "@/lib/api";
import { money, dateFr, QSTATUS } from "@/lib/format";
import LineItemsEditor from "@/components/LineItemsEditor";
import SendDialog from "@/components/SendDialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Plus, DotsThreeVertical, FilePdf, PaperPlaneTilt, CheckCircle, XCircle, ArrowsLeftRight, PencilSimple, Trash } from "@phosphor-icons/react";

const emptyForm = { client_id: "", issue_date: "", valid_until: "", notes: "", status: "draft" };
const newItems = () => [{ description: "", quantity: 1, unit_price: 0, taxable: true }];

function QBadge({ status }) {
  const s = QSTATUS[status] || QSTATUS.draft;
  return <span data-testid={`qstatus-${status}`} className={`inline-flex items-center rounded-sm border px-2 py-0.5 text-xs font-medium ${s.cls}`}>{s.label}</span>;
}

export default function Quotes() {
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [items, setItems] = useState(newItems());
  const [editId, setEditId] = useState(null);
  const [delId, setDelId] = useState(null);
  const [sendTarget, setSendTarget] = useState(null);

  const load = () => api.get("/quotes").then((r) => setQuotes(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/clients").then((r) => setClients(r.data)).catch(() => {}); }, []);

  const openNew = () => { setForm(emptyForm); setItems(newItems()); setEditId(null); setOpen(true); };
  const openEdit = (q) => {
    setForm({ client_id: q.client_id, issue_date: q.issue_date, valid_until: q.valid_until, notes: q.notes || "", status: q.status });
    setItems(q.line_items.map((i) => ({ ...i }))); setEditId(q.id); setOpen(true);
  };

  const save = async () => {
    if (!form.client_id) { toast.error("Sélectionnez un client."); return; }
    if (!items.some((i) => i.description)) { toast.error("Ajoutez au moins une ligne."); return; }
    const payload = {
      client_id: form.client_id, issue_date: form.issue_date || null, valid_until: form.valid_until || null,
      notes: form.notes, status: form.status,
      line_items: items.map((i) => ({ description: i.description, quantity: Number(i.quantity) || 0, unit_price: Number(i.unit_price) || 0, taxable: i.taxable !== false })),
    };
    try {
      if (editId) await api.put(`/quotes/${editId}`, payload);
      else await api.post("/quotes", payload);
      toast.success(editId ? "Soumission mise à jour" : "Soumission créée"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const viewPdf = (q) => window.open(`${API}/quotes/${q.id}/pdf?auth=${localStorage.getItem("token")}`, "_blank");
  const send = (q) => setSendTarget({ kind: "quote", doc: q });
  const setStatus = async (q, status) => { try { await api.post(`/quotes/${q.id}/status`, { status }); toast.success("Statut mis à jour"); load(); } catch (e) { toast.error(apiError(e)); } };
  const convert = async (q) => { try { const { data } = await api.post(`/quotes/${q.id}/convert`); toast.success(`Convertie en facture ${data.invoice_number}`); load(); } catch (e) { toast.error(apiError(e)); } };
  const del = async () => { try { await api.delete(`/quotes/${delId}`); toast.success("Soumission supprimée"); setDelId(null); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="space-y-6" data-testid="quotes-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tighter">Soumissions</h1>
          <p className="text-muted-foreground mt-1">Devis convertibles en factures.</p>
        </div>
        <Button data-testid="new-quote-btn" className="rounded-sm" onClick={openNew}><Plus size={18} className="mr-1" /> Nouvelle soumission</Button>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead>Numéro</TableHead><TableHead>Client</TableHead><TableHead>Date</TableHead>
              <TableHead>Valide jusqu'au</TableHead><TableHead className="text-right">Total</TableHead>
              <TableHead>Statut</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {quotes.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">Aucune soumission.</TableCell></TableRow>}
            {quotes.map((q) => (
              <TableRow key={q.id} data-testid={`quote-row-${q.id}`}>
                <TableCell className="font-mono font-medium">{q.quote_number}</TableCell>
                <TableCell>{q.client?.name}</TableCell>
                <TableCell className="text-muted-foreground text-sm">{dateFr(q.issue_date)}</TableCell>
                <TableCell className="text-muted-foreground text-sm">{dateFr(q.valid_until)}</TableCell>
                <TableCell className="text-right tabular-nums font-medium">{money(q.total)}</TableCell>
                <TableCell><QBadge status={q.status} /></TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button data-testid={`quote-actions-${q.id}`} variant="ghost" size="icon" className="h-8 w-8"><DotsThreeVertical size={18} weight="bold" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="rounded-sm">
                      <DropdownMenuItem onClick={() => viewPdf(q)}><FilePdf size={16} className="mr-2" /> Voir le PDF</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => send(q)}><PaperPlaneTilt size={16} className="mr-2" /> Envoyer</DropdownMenuItem>
                      {q.status !== "converted" && <DropdownMenuItem data-testid={`convert-${q.id}`} onClick={() => convert(q)}><ArrowsLeftRight size={16} className="mr-2" /> Convertir en facture</DropdownMenuItem>}
                      <DropdownMenuItem onClick={() => setStatus(q, "accepted")}><CheckCircle size={16} className="mr-2" /> Marquer acceptée</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setStatus(q, "declined")}><XCircle size={16} className="mr-2" /> Marquer refusée</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openEdit(q)}><PencilSimple size={16} className="mr-2" /> Modifier</DropdownMenuItem>
                      <DropdownMenuItem className="text-destructive" onClick={() => setDelId(q.id)}><Trash size={16} className="mr-2" /> Supprimer</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Modifier la soumission" : "Nouvelle soumission"}</DialogTitle></DialogHeader>
          <div className="space-y-5">
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Client *</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                  <SelectTrigger data-testid="quote-client-select" className="rounded-sm"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Date</Label>
                <Input type="date" className="rounded-sm" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Valide jusqu'au</Label>
                <Input type="date" className="rounded-sm" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} /></div>
            </div>
            <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2 block">Lignes</Label><LineItemsEditor items={items} setItems={setItems} /></div>
            <Textarea data-testid="quote-notes" className="rounded-sm" placeholder="Notes (ex : exclusions, acompte requis)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Annuler</Button>
            <Button data-testid="save-quote-btn" className="rounded-sm" onClick={save}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SendDialog target={sendTarget} onOpenChange={(v) => !v && setSendTarget(null)} onSent={load} />
      <AlertDialog open={!!delId} onOpenChange={(v) => !v && setDelId(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader><AlertDialogTitle>Supprimer cette soumission ?</AlertDialogTitle><AlertDialogDescription>Action irréversible.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="rounded-sm">Annuler</AlertDialogCancel>
            <AlertDialogAction className="rounded-sm bg-destructive hover:bg-destructive/90" onClick={del}>Supprimer</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
